/**
 * NewMessage.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Chat input component. When the network is disconnected the send button and
 * the Enter-key handler are disabled so users cannot silently lose messages.
 * A small inline notice replaces the input placeholder to explain why.
 *
 * All existing logic (onChange, onKeyDown, sendMessage) is preserved unchanged
 * except for the guard that checks networkStatus from Redux.
 */

import React, { useState } from "react";
import { connect } from "react-redux";
import send_message_btn from "../../resources/images/sendMessageButton.svg";
import * as webRTCHandler from "../../utils/webRTCHandler";
import { NETWORK_STATUS } from "../../utils/networkConnectionManager";

const NewMessage = ({ networkStatus }) => {
  const [message, setMessage] = useState("");

  const isDisabled =
    networkStatus === NETWORK_STATUS.RECONNECTING ||
    networkStatus === NETWORK_STATUS.FAILED;

  const handleTextChange = (event) => {
    setMessage(event.target.value);
  };

  const handleKeyPressed = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      if (!isDisabled) {
        sendMessage();
      }
    }
  };

  const sendMessage = () => {
    if (isDisabled) return;
    if (message.trim().length > 0) {
      webRTCHandler.sendMessageUsingDataChannel(message);
      setMessage("");
    }
  };

  return (
    <div className="new_message_container">
      <input
        className={`new_message_input${isDisabled ? " new_message_input--disabled" : ""}`}
        value={message}
        onChange={handleTextChange}
        placeholder={
          isDisabled
            ? "Chat unavailable — reconnecting…"
            : "Type your message..."
        }
        onKeyDown={handleKeyPressed}
        disabled={isDisabled}
        aria-disabled={isDisabled}
        aria-label={
          isDisabled
            ? "Chat input disabled — network reconnecting"
            : "Type a chat message"
        }
      />
      <img
        className={`new_message_button${isDisabled ? " new_message_button--disabled" : ""}`}
        src={send_message_btn}
        alt="Send Message"
        onClick={isDisabled ? undefined : sendMessage}
        style={isDisabled ? { opacity: 0.35, cursor: "not-allowed", pointerEvents: "none" } : {}}
        aria-hidden={isDisabled}
      />
    </div>
  );
};

const mapStoreStateToProps = (state) => ({
  networkStatus: state.networkStatus,
});

export default connect(mapStoreStateToProps)(NewMessage);